import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { apiClient } from '@api/client';
import { useAuthStore } from '@store/authStore';
import type { SlackConnection } from '@store/authStore';
import { useNotificationStore } from '@store/notificationStore';

interface SlackAuthorizationUrlResponse {
  authorization_url: string;
}

const extractErrorMessage = (error: unknown): string => {
  if (error instanceof AxiosError) {
    return error.response?.data?.detail || 'Slack request failed';
  }
  return 'Slack request failed';
};

export const useConnectSlack = () => {
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (returnPath: string) => {
      const { data } = await apiClient.get<SlackAuthorizationUrlResponse>(
        `/auth/slack/url?return_path=${encodeURIComponent(returnPath)}`
      );
      return data;
    },
    onError: (error) => {
      addNotification({
        message: extractErrorMessage(error),
        type: 'error',
      });
    },
  });
};

export const useDisconnectSlack = () => {
  const queryClient = useQueryClient();
  const { currentUser, setCurrentUser } = useAuthStore();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<SlackConnection>('/auth/slack/disconnect');
      return data;
    },
    onSuccess: (slackConnection) => {
      if (currentUser) {
        setCurrentUser({ ...currentUser, slack_connection: slackConnection });
      }
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
      addNotification({
        message: 'Slack disconnected',
        type: 'success',
      });
    },
    onError: (error) => {
      addNotification({
        message: extractErrorMessage(error),
        type: 'error',
      });
    },
  });
};
